import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";
import { Avatar } from "../../components/account/Avatar";
import { Button } from "../../components/tailwind/Button";
import { authSelectors } from "../../store/auth/selector";
import {
  CreateCommentInput,
  useCreateCommentMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";

export function TaskCommentInput(props: {
  input: CreateCommentInput;
  onCommented: () => void;
}) {
  const account = useSelector(authSelectors.account);
  const client = useGqlClient();
  const createComment = useCreateCommentMutation(client);

  const [comment, setComment] = useState(props.input.comment);
  const [isFocused, setIsFocused] = useState(false);
  const createCommentRef = useRef<HTMLButtonElement>(null);

  function onSubmit() {
    if (comment.trim().length === 0) {
      return;
    }

    createComment.mutate(
      {
        input: {
          ...props.input,
          comment,
          files: props.input.files ?? [],
        },
      },
      {
        onSuccess: () => {
          props.onCommented();
        },
      },
    );

    setComment("");
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      createCommentRef.current?.click();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setTimeout(() => {
      if (
        !document.activeElement ||
        !document.activeElement.closest("#task_comment_form")
      ) {
        setIsFocused(false);
      }
    }, 0);
  };

  if (!account) {
    return null;
  }

  return (
    <div className="mt-6 flex gap-x-3" onBlur={handleBlur}>
      <Avatar account={account} />
      <form
        id="task_comment_form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
        className="relative flex-auto"
      >
        <div
          className={`overflow-hidden rounded-lg ${isFocused ? "pb-12" : "pb-1"} shadow-sm ring-1 ring-inset transition-all ease-in-out duration-300 ring-gray-300 focus-within:bg-white focus-within:ring-indigo-500 focus:ring-2`}
        >
          <label htmlFor="comment" className="sr-only">
            Add your comment
          </label>
          <MentionsInput
            rows={isFocused ? 2 : 1}
            name="comment"
            id="comment"
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="mentions"
            placeholder="Add your comment..."
            value={comment}
            onChange={(_, newValue) => {
              setComment(newValue);
            }}
            defaultValue=""
          >
            <Mention
              trigger="@"
              data={
                account.firm
                  ? account.firm.accounts.map((acc) => ({
                      id: acc.id,
                      display: acc.name,
                    }))
                  : []
              }
              className="mentions__mention"
              appendSpaceOnAdd
              renderSuggestion={(suggestion) => {
                const acc = account.firm?.accounts.find(
                  (a) => a.id === suggestion.id,
                );
                return (
                  <div className="flex items-center gap-x-2">
                    {acc?.avatar ? <Avatar account={acc} size="xs" /> : null}
                    <p>{suggestion.display}</p>
                  </div>
                );
              }}
            />
          </MentionsInput>
        </div>

        <div
          className={`ring-0 absolute inset-x-0 bottom-0 flex justify-end py-2 pl-3 pr-2 transition-all ease-in-out ${isFocused ? "opacity-100 visible duration-300 delay-100" : "opacity-0 invisible duration-150 delay-0"}`}
        >
          <Button
            ref={createCommentRef}
            variant="neutral"
            size="s"
            text="Comment"
            isLoading={createComment.isPending}
            loadingText="Commenting..."
            type="submit"
          />
        </div>
      </form>
    </div>
  );
}
